import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { certificationAPI } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Award,
  Check,
  X,
  BookOpen,
  FileText,
  CheckSquare,
  ClipboardList,
  ArrowRight,
  Download,
  Lock,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_exec-talent-hub-1/artifacts/7a127wcx_Color%20logo%20-%20no%20background.svg";

export default function CertificationPage() {
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await certificationAPI.get();
        setCert(res.data);
      } catch (err) {
        console.error("Failed to load certification:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-slate-400 text-sm uppercase tracking-wider">Loading...</div>
      </div>
    );
  }

  if (!cert) return null;

  const sectionIcons = {
    learning: BookOpen,
    sops: FileText,
    checklists: CheckSquare,
    assessment_passed: ClipboardList,
  };

  const sectionLabels = {
    learning: "Learning",
    sops: "SOPs & Templates",
    checklists: "Checklists",
    assessment_passed: "Assessment",
  };

  return (
    <div className="space-y-8" data-testid="certification-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
          Certification Progress
        </h1>
        <p className="text-slate-500 mt-2 text-base font-body">
          Complete all {cert.total_modules} modules to earn your Certified Axentia Global Executive Search Consultant credential.
        </p>
      </div>

      {/* Certificate Card */}
      {cert.certified ? (
        <div
          data-testid="certification-card-complete"
          className="bg-slate-900 text-white rounded-sm p-8 text-center"
        >
          <img src={LOGO_URL} alt="Axentia Global" className="h-10 mx-auto mb-4 brightness-0 invert" />
          <Award className="w-12 h-12 mx-auto mb-4 text-amber-400" strokeWidth={1} />
          <h2 className="font-heading text-2xl font-bold mb-2">
            Certified Axentia Global Executive Search Consultant
          </h2>
          <p className="text-slate-400 text-sm font-body">
            Congratulations. You have completed all {cert.total_modules} modules and passed all assessments.
          </p>
          <Separator className="my-6 bg-slate-700" />
          <div className="text-sm text-slate-400 mb-6">
            This certification confirms competency across the full executive search lifecycle.
          </div>
          <Button
            data-testid="download-certificate"
            onClick={() => {
              const token = localStorage.getItem("axentia_token");
              fetch(certificationAPI.downloadUrl(), {
                headers: { Authorization: `Bearer ${token}` },
              })
                .then((r) => r.blob())
                .then((blob) => {
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "Axentia_Global_Certificate.pdf";
                  a.click();
                  window.URL.revokeObjectURL(url);
                });
            }}
            className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm px-6"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Certificate PDF
          </Button>
        </div>
      ) : (
        <div
          data-testid="certification-card-progress"
          className="bg-white border border-slate-200 rounded-sm p-6 shadow-[0_1px_3px_0_rgb(0,0,0,0.05)]"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center">
              <Award className="w-6 h-6 text-slate-600" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="font-heading text-lg font-semibold text-slate-900">
                Certification In Progress
              </h2>
              <p className="text-sm text-slate-500">
                {cert.completed_modules} of {cert.total_modules} modules complete
              </p>
            </div>
          </div>
          <Progress value={cert.completion_percentage} className="h-2 mb-2" />
          <p className="text-xs text-slate-400 text-right">{cert.completion_percentage}% complete</p>

          <Separator className="my-5" />
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-slate-500">
              {cert.total_modules - cert.completed_modules} module{cert.total_modules - cert.completed_modules === 1 ? "" : "s"} remaining to unlock your certificate.
            </p>
            <Button
              data-testid="download-certificate-locked"
              disabled
              className="bg-slate-200 text-slate-400 rounded-sm px-6 cursor-not-allowed"
            >
              <Lock className="w-4 h-4 mr-2" />
              Certificate Locked
            </Button>
          </div>
        </div>
      )}

      {/* Module Status Table */}
      <div>
        <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
          Module Completion Status
        </h2>
        <div className="bg-white border border-slate-200 rounded-sm shadow-[0_1px_3px_0_rgb(0,0,0,0.05)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">#</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Module</th>
                  <th className="text-center px-3 py-3 font-semibold text-slate-700">Learning</th>
                  <th className="text-center px-3 py-3 font-semibold text-slate-700">SOPs</th>
                  <th className="text-center px-3 py-3 font-semibold text-slate-700">Checklists</th>
                  <th className="text-center px-3 py-3 font-semibold text-slate-700">Assessment</th>
                  <th className="text-center px-3 py-3 font-semibold text-slate-700">Score</th>
                  <th className="text-center px-3 py-3 font-semibold text-slate-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {cert.module_status.map((mod) => (
                  <tr
                    key={mod.id}
                    data-testid={`cert-module-row-${mod.id}`}
                    className="border-b border-slate-100 hover:bg-slate-50/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/modules/${mod.id}`)}
                  >
                    <td className="px-4 py-3 text-slate-400 font-medium">{mod.id}</td>
                    <td className="px-4 py-3 text-slate-900 font-medium">{mod.title}</td>
                    {["learning", "sops", "checklists", "assessment_passed"].map((key) => (
                      <td key={key} className="text-center px-3 py-3">
                        {mod[key] ? (
                          <Check className="w-4 h-4 text-emerald-600 mx-auto" />
                        ) : (
                          <X className="w-4 h-4 text-slate-300 mx-auto" />
                        )}
                      </td>
                    ))}
                    <td className="text-center px-3 py-3 text-sm text-slate-600">
                      {mod.assessment_score !== null ? `${mod.assessment_score}%` : "—"}
                    </td>
                    <td className="text-center px-3 py-3">
                      {mod.completed ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                          Done
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          Pending
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
